/**
 * 支付宝 H5 中台对接（form 模式）
 * 与微信共用系统标识 attach=aigc。
 * 下游请求中台 pc-form / wap-form 获取 formHtml，在本系统页面渲染并提交，即跳转支付宝收银台。
 * 轮询中台订单状态接口 /api/alipay/order/status。
 */

/** 支付中台地址（与微信同域）；HTTPS 页面必须用 HTTPS，否则混合内容会被浏览器阻止 */
const PAY_CENTER_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PAY_CENTER_URL) || 'https://pay.jiuanci.com';
const _base = PAY_CENTER_BASE.replace(/\/$/, '');

/** 中台登记的系统名称，与微信一致 */
export const ALIPAY_ATTACH = 'aigc';

/** PC 支付 form 接口 */
const ALIPAY_PC_FORM = `${_base}/api/alipay/pc-form`;
/** 手机支付 form 接口 */
const ALIPAY_WAP_FORM = `${_base}/api/alipay/wap-form`;
/** 订单状态查询 */
const ALIPAY_ORDER_STATUS_BASE = `${_base}/api/alipay/order/status`;
/** 关单接口（支付取消/失败后关闭支付宝侧订单） */
const ALIPAY_ORDER_CLOSE_BASE = `${_base}/api/alipay/order/close`;
/** 申请退款接口：POST {中台基地址}/api/alipay/refund */
const ALIPAY_REFUND_BASE = `${_base}/api/alipay/refund`;

export interface AlipayFormParams {
  outTradeNo: string;
  totalCents: number;
  description: string;
  userId: string;
  redirect?: string;
  cancel?: string;
  timeExpire?: string;
}

/**
 * 请求中台 PC 支付 form（alipay.trade.page.pay）。
 * GET 返回 JSON { formHtml }，下游将 formHtml 插入页面并 form.submit() 即跳转支付宝 PC 收银台。
 */
export async function fetchAlipayPcForm(
  params: AlipayFormParams
): Promise<{ ok: boolean; formHtml?: string; error?: string }> {
  try {
    const q = new URLSearchParams();
    q.set('out_trade_no', params.outTradeNo);
    q.set('attach', ALIPAY_ATTACH);
    q.set('total', String(params.totalCents));
    q.set('description', params.description);
    q.set('user_id', params.userId);
    if (params.redirect != null && params.redirect !== '') q.set('redirect', params.redirect);
    if (params.timeExpire != null && params.timeExpire !== '') q.set('time_expire', params.timeExpire);
    const url = `${ALIPAY_PC_FORM}?${q.toString()}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({})) as { formHtml?: string; code?: string; message?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    const formHtml = data.formHtml;
    if (typeof formHtml !== 'string' || !formHtml) {
      return { ok: false, error: data.message || '未返回 formHtml' };
    }
    return { ok: true, formHtml };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}

/**
 * 请求中台手机支付 form（alipay.trade.wap.pay）。
 * GET 返回 JSON { formHtml }，下游将 formHtml 插入页面并 form.submit() 即跳转支付宝手机收银台或唤起 APP。
 */
export async function fetchAlipayWapForm(
  params: AlipayFormParams
): Promise<{ ok: boolean; formHtml?: string; error?: string }> {
  try {
    const q = new URLSearchParams();
    q.set('out_trade_no', params.outTradeNo);
    q.set('attach', ALIPAY_ATTACH);
    q.set('total', String(params.totalCents));
    q.set('description', params.description);
    q.set('user_id', params.userId);
    if (params.redirect != null && params.redirect !== '') q.set('redirect', params.redirect);
    if (params.cancel != null && params.cancel !== '') q.set('cancel', params.cancel);
    if (params.timeExpire != null && params.timeExpire !== '') q.set('time_expire', params.timeExpire);
    const url = `${ALIPAY_WAP_FORM}?${q.toString()}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({})) as { formHtml?: string; code?: string; message?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    const formHtml = data.formHtml;
    if (typeof formHtml !== 'string' || !formHtml) {
      return { ok: false, error: data.message || '未返回 formHtml' };
    }
    return { ok: true, formHtml };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}

/**
 * 调用中台关单接口：关闭支付宝侧订单（支付取消/失败后调用）。
 * GET {中台基地址}/api/alipay/order/close?out_trade_no={商户订单号}
 */
export async function closeAlipayOrder(outTradeNo: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const url = `${ALIPAY_ORDER_CLOSE_BASE}${ALIPAY_ORDER_CLOSE_BASE.includes('?') ? '&' : '?'}out_trade_no=${encodeURIComponent(outTradeNo)}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({})) as { code?: string; message?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    if (data.code === 'SUCCESS') {
      return { ok: true };
    }
    return { ok: false, error: data.message || data.code || '关单未成功' };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}

/** 支付宝申请退款参数：金额单位均为分 */
export interface AlipayRefundParams {
  outTradeNo: string;
  refundAmountCents: number;
  refundReason?: string;
  outRequestNo?: string;
}

/**
 * 调用中台支付宝申请退款接口（alipay.trade.refund）。
 * POST {中台基地址}/api/alipay/refund
 */
export async function requestAlipayRefund(
  params: AlipayRefundParams
): Promise<{ ok: boolean; error?: string }> {
  try {
    const body: Record<string, unknown> = {
      out_trade_no: params.outTradeNo,
      refund_amount: params.refundAmountCents,
    };
    if (params.refundReason != null && params.refundReason !== '') body.refund_reason = params.refundReason;
    if (params.outRequestNo != null && params.outRequestNo !== '') body.out_request_no = params.outRequestNo;
    const res = await fetch(ALIPAY_REFUND_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as { code?: string; message?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    if (data.code === 'SUCCESS') {
      return { ok: true };
    }
    return { ok: false, error: data.message || data.code || '退款未成功' };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : '网络错误';
    return { ok: false, error: msg };
  }
}

/**
 * 将中台返回的 formHtml 插入页面并提交，即跳转支付宝收银台。
 * 插入到 document.body，取第一个 form 并 submit；若 form 内带 script 会自动提交则仅插入即可。
 */
export function submitAlipayForm(formHtml: string): void {
  const container = document.createElement('div');
  container.innerHTML = formHtml;
  document.body.appendChild(container);
  const form = container.querySelector('form');
  if (form) {
    form.submit();
  }
}

/** 支付宝订单状态查询结果：code=SUCCESS 时含 trade_status、trade_no */
export interface AlipayOrderStatusResult {
  trade_status: string;
  trade_no?: string;
}

/**
 * 查询支付宝订单状态（轮询用）。
 * 中台约定：一律 HTTP 200，body.code 区分；SUCCESS 时 trade_status === 'TRADE_SUCCESS' 视为支付成功。
 */
export async function queryAlipayOrderStatus(
  outTradeNo: string
): Promise<{ ok: boolean; data?: AlipayOrderStatusResult; error?: string }> {
  try {
    const url = `${ALIPAY_ORDER_STATUS_BASE}${ALIPAY_ORDER_STATUS_BASE.includes('?') ? '&' : '?'}out_trade_no=${encodeURIComponent(outTradeNo)}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({})) as { code?: string; message?: string; trade_status?: string; trade_no?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    if (data.code === 'SUCCESS') {
      const trade_status = data.trade_status ?? '';
      const trade_no = data.trade_no;
      return { ok: true, data: { trade_status, trade_no } };
    }
    return { ok: false, error: data.message || data.code || '查询未成功' };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}
